export type GamePhase =
  | 'lobby'
  | 'discussion'
  | 'rankerRanking'
  | 'groupGuess'
  | 'individualGuess'
  | 'revealing'
  | 'lineReveal'
  | 'results'

export type GameMode = 'individual' | 'group'

export interface RankingPayload {
  ranking: string[]
  lineIndex: number
}

export const ROOM_NAME = 'preferences'

export type ClientMessage =
  | { type: 'add_to_bowl'; payload: { items: string[] } }
  | { type: 'clear_bowl'; payload: Record<string, never> }
  | { type: 'start_game'; payload: Record<string, never> }
  | { type: 'set_game_mode'; payload: { mode: GameMode } }
  | { type: 'submit_ranker_ranking'; payload: RankingPayload }
  | { type: 'submit_group_guess'; payload: RankingPayload }
  | { type: 'submit_individual_guess'; payload: RankingPayload }
  | { type: 'start_discussion_to_ranking'; payload: Record<string, never> }
  | { type: 'advance_to_reveal'; payload: Record<string, never> }
  | { type: 'reveal_next'; payload: Record<string, never> }
  | { type: 'reveal_line'; payload: Record<string, never> }
  | { type: 'next_round'; payload: Record<string, never> }
  | { type: 'kick_player'; payload: { playerId: string } }
  | { type: 'request_host'; payload: Record<string, never> }
  | { type: 'cancel_host_request'; payload: Record<string, never> }
  | { type: 'approve_host_request'; payload: Record<string, never> }
  | { type: 'decline_host_request'; payload: Record<string, never> }
  | { type: 'pick_host'; payload: { targetUserId?: string } }
  | { type: 'change_name'; payload: { name: string } }
  | { type: 'end_game'; payload: Record<string, never> }
  | { type: 'skip_ranker'; payload: Record<string, never> }
  | { type: 'reset_leaderboard'; payload: Record<string, never> }
  | { type: 'reassign_seat'; payload: { droppedUserId: string; newOwnerUserId: string } }
  | { type: 'request_seat_takeover'; payload: { targetUserId: string } }
  | { type: 'cancel_seat_takeover'; payload: Record<string, never> }
  | { type: 'approve_seat_takeover'; payload: Record<string, never> }
  | { type: 'decline_seat_takeover'; payload: Record<string, never> }
  | { type: 'assign_ranker'; payload: { playerId: string } }
  | { type: 'add_reaction'; payload: { itemText: string; emoji: string } }
  | { type: 'remove_reaction'; payload: { itemText: string; emoji: string } }

export type MessageType = ClientMessage['type']

export const MIN_PLAYERS = 3
export const MIN_BOWL_ITEMS = 5
export const ITEMS_PER_ROUND = 5
export const RECONNECTION_WINDOW_SECONDS = 120

// Feature flag — leave false to keep gameplay locked to individual voting.
// When false: the group/consensus mode is unreachable, the lobby and menu
// hide the mode toggle, the lobby hides the "Game mode" display, the
// instructions skip the group-mode bullet, and the server rejects any
// set_game_mode message asking for 'group'. Flip to true to bring the
// second mode back without any other code change.
export const GROUP_MODE_ENABLED = false

// Quick-pick row shown inline in the long-press popover. Six common reactions
// that cover most cases without scrolling.
export const QUICK_REACTIONS = ['😂', '❤️', '👍', '👎', '😱', '🤢'] as const

// Full allowlist surfaced behind the "+" button in the picker. Anything not
// in this list is rejected server-side. Keeping it a curated allowlist keeps
// the data clean (no arbitrary text smuggled in as a "reaction") while still
// giving players plenty of range.
export const ALLOWED_REACTIONS = [
  '😂', '🤣', '❤️', '👍', '👎', '😱',
  '🤢', '😅', '😭', '🙏', '🙌', '👏',
  '💀', '🔥', '💯', '⭐', '✨', '🎉',
  '💖', '🥹', '🥺', '🤔', '😍', '🤩',
  '😎', '😏', '🫠', '🤯', '🤡', '👀',
  '👌', '🤘', '✌️', '🫶', '🚀', '🤤',
  '😡', '🙄', '🤷', '🤝', '🥲', '💩',
  '😴', '🥵', '🥶', '🤮', '🎊', '🦄',
] as const
export type AllowedReaction = (typeof ALLOWED_REACTIONS)[number]

export const ROOM_WORDS = [
  'BEAN','BEAR','BIRD','BLUE','BOAT','BONE','BOOK','BOOT','CAKE','CAMP',
  'CARD','CAVE','CHEF','CLAM','CLAY','COIN','COOK','COOL','CORN','COZY',
  'CRAB','CROW','CUBE','DART','DAWN','DEER','DICE','DOCK','DOVE','DRUM',
  'DUCK','DUNE','DUST','FAWN','FERN','FIRE','FISH','FLAG','FROG','FUEL',
  'GAME','GIFT','GOAT','GOLD','GOLF','GLOW','GRAPE','GUST','HARE','HAWK',
  'HAZE','HERO','HILL','HIVE','HOOD','HOOK','HOOT','HORN','IDEA','IRIS',
  'JADE','JAZZ','JEST','JOKE','JUMP','JUNK','KELP','KITE','KNOB','KNOT',
  'LACE','LAKE','LAMB','LAMP','LANE','LARK','LEAF','LIME','LION','LOCK',
  'LOFT','LOOP','LUCK','LYNX','MALT','MASK','MAZE','MILK','MINT','MIST',
  'MOON','MOSS','MOTH','MULE','MUSE','NEST','NEWT','NOON','NOTE','OAKS',
  'OPAL','ORCA','OVEN','PALM','PAWS','PEAK','PEAR','PINE','PLUM','POEM',
  'POND','PONY','RAIN','REEF','RICE','RING','ROAD','ROCK','ROPE','ROSE',
  'RUBY','RUST','SAGE','SAND','SEAL','SEED','SILK','SNOW','SOAP','SOCK',
  'SONG','STAR','STEM','SURF','SWAN','TACO','TALL','TEAL','TIDE','TOAD',
  'TREE','TUBA','TUNE','TUSK','VALE','VEIL','VEST','VINE','VOID','WADE',
  'WAND','WASP','WAVE','WEED','WELL','WICK','WILD','WIND','WING','WISH',
  'WOLF','WOOD','WOOL','WORM','YARN','YAWN','YOGA','YOLK','ZINC','ZONE',
] as const
